// Universal site-search engine.
//
// Performs case- and accent-insensitive substring matching across the user-facing fields
// of the four searchable entity types (organizations, events, regulations,
// articles). The result is grouped into ordered, per-type sections that the
// navbar search modal renders as collapsible tables.
//
// The engine is intentionally pure: callers pass in the already-fetched data
// (RTK Query handles freshness/caching upstream) plus a visibility map, and
// receive back fully-built sections including detail-page URLs and per-field
// match snippets. Adding a fifth type later (e.g. profiles) only requires a
// new field-descriptor list plus a URL builder.

import { normalizeSearchText } from "@/utils/searchText";

/** A single field on an entity that matched the search term. */

/** One matching entity (a single row in the results table). */

/** A per-type group of results. */

/** Visibility flags mirroring the site settings toggles. */

const SNIPPET_MAX = 300;

/** Coerce an unknown value into a trimmed display string (empty when blank). */
function toText(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

/**
 * Build a snippet centred on the first match of `term` within `value`,
 * capped at SNIPPET_MAX characters with leading/trailing ellipses as needed.
 */
function buildSnippet(value, term) {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= SNIPPET_MAX) return normalized;
  const idx = normalized.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) {
    return `${normalized.slice(0, SNIPPET_MAX)}…`;
  }

  // Centre the window around the match.
  const half = Math.floor((SNIPPET_MAX - term.length) / 2);
  let start = Math.max(0, idx - half);
  let end = Math.min(normalized.length, start + SNIPPET_MAX);
  start = Math.max(0, end - SNIPPET_MAX);
  let snippet = normalized.slice(start, end);
  if (start > 0) snippet = `…${snippet}`;
  if (end < normalized.length) snippet = `${snippet}…`;
  return snippet;
}

/** Strip HTML tags so rich content can be matched/snipped as plain text. */
function stripHtml(html) {
  return html.replace(/<[^>]*>/g, " ").replace(/&[a-z]+;/gi, " ").replace(/\s+/g, " ").trim();
}

/** A field descriptor: a label plus a getter returning candidate strings. */

/**
 * Test all of an entity's field descriptors against `term` and collect the
 * matches into MatchedField entries (one per matching label, first hit wins
 * for the snippet). Returns an empty array when nothing matched.
 */
function collectMatches(item, descriptors, lowerTerm, term) {
  const matches = [];
  for (const descriptor of descriptors) {
    let values;
    try {
      values = descriptor.getValues(item);
    } catch {
      values = [];
    }
    for (const raw of values) {
      const text = toText(raw);
      if (!text) continue;
      if (normalizeSearchText(text).includes(normalizeSearchText(lowerTerm))) {
        matches.push({
          label: descriptor.label,
          value: text,
          snippet: buildSnippet(text, term)
        });
        break; // one match per label is enough
      }
    }
  }
  return matches;
}

// ── URL builders ──────────────────────────────────────────────────────────

function organizationUrl(org) {
  const id = org.backendId || org.id || org.slug;
  return `/organizations/${id}`;
}
function eventUrl(evt) {
  return `/events/${evt.slug || evt.id}`;
}
function regulationUrl(reg) {
  return `/regulations/${reg.slug || reg.id}`;
}
function articleUrl(art) {
  return `/articles/${art.slug || art.id}`;
}

// ── Field descriptors ─────────────────────────────────────────────────────

const organizationFields = [{
  label: "Name",
  getValues: o => [o.orgName]
}, {
  label: "Description",
  getValues: o => [o.description]
},
// Free-text body fields — a user searching a phrase they wrote into
// "About us" expects to find the page.
{
  label: "About",
  getValues: o => [o.orgDescripTitle ?? "", o.orgDescrip ?? ""]
}, {
  label: "Services",
  getValues: o => (o.services ?? []).flatMap(svc => [String(svc?.title ?? ""), String(svc?.description ?? ""), String(svc?.category ?? "")])
}, {
  // The address fields already have their own "Location" descriptor
  // further down — this one only adds the contact company name.
  label: "Company",
  getValues: o => [o.infoSection?.contact?.company ?? ""]
}, {
  label: "Organization Type",
  getValues: o => o.orgType ?? []
}, {
  label: "Industry Focus",
  getValues: o => o.orgIndustryFocus ?? []
}, {
  label: "Subject Focus",
  getValues: o => o.orgFocus ?? []
}, {
  label: "Product Expertise",
  getValues: o => o.productFocus ?? []
}, {
  label: "Regional Expertise",
  getValues: o => o.regionalFocus ?? []
}, {
  label: "Website",
  getValues: o => [o.website]
}, {
  label: "Location",
  getValues: o => {
    const loc = o.infoSection?.location;
    if (!loc) return [];
    return [loc.city, loc.address ?? "", loc.address2 ?? "", loc.state ?? "", loc.postalCode ?? "", loc.country ?? ""].filter(Boolean);
  }
}];

/** Shared descriptors for regulations and articles (identical shapes). */
function buildBriefFields() {
  return [{
    label: "Title",
    getValues: r => [r.title]
  }, {
    label: "Short Title",
    getValues: r => [r.shortTitle ?? ""]
  }, {
    label: "Description",
    getValues: r => [r.description ?? ""]
  }, {
    label: "Category",
    getValues: r => [toText(r.payload?.category)]
  }, {
    label: "Tags",
    getValues: r => {
      const p = r.payload ?? {};
      return [...(Array.isArray(p.tags) ? p.tags : []), ...(Array.isArray(p.filterTags) ? p.filterTags : []), ...(Array.isArray(p.miscTags) ? p.miscTags : [])].map(toText);
    }
  }, {
    label: "Regions",
    getValues: r => {
      const p = r.payload ?? {};
      return [...(Array.isArray(p.regions) ? p.regions : []), ...(Array.isArray(p.regionTags) ? p.regionTags : [])].map(toText);
    }
  }, {
    label: "Summary",
    getValues: r => [toText(r.payload?.executiveSummary)]
  }, {
    label: "Section Content",
    getValues: r => {
      const sections = r.payload?.sections;
      if (!Array.isArray(sections)) return [];
      const out = [];
      for (const section of sections) {
        if (!section || typeof section !== "object") continue;
        const title = toText(section.title);
        if (title) out.push(title);
        const content = section.content;
        if (typeof content === "string" && content) {
          out.push(stripHtml(content));
        }
      }
      return out;
    }
  }];
}
const eventFields = [{
  label: "Title",
  getValues: e => [e.title]
}, {
  label: "Description",
  getValues: e => [e.description ?? ""]
}, {
  label: "Category",
  getValues: e => [toText(e.payload?.category)]
}, {
  label: "Tags",
  getValues: e => {
    const tags = e.payload?.tags;
    return Array.isArray(tags) ? tags.map(toText) : [];
  }
}, {
  label: "Organizer",
  getValues: e => [toText(e.payload?.organizer?.name)]
}, {
  label: "Location",
  getValues: e => {
    const loc = e.payload?.location;
    if (!loc || typeof loc !== "object") return [];
    return [toText(loc.venue), toText(loc.address), toText(loc.city), toText(loc.state), toText(loc.country)].filter(Boolean);
  }
}];

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Search all provided datasets for `term` and return ordered, non-empty-aware
 * sections (empty sections are still returned so callers can show counts;
 * filter them out at render time if desired). Types disabled via `visibility`
 * are omitted entirely.
 */
export function searchSite(term, datasets, visibility) {
  const trimmed = term.trim();
  const sections = [];
  // A term that normalises to nothing (only punctuation/whitespace) would
  // substring-match every record — treat it as no query.
  if (!trimmed || !normalizeSearchText(trimmed)) return sections;
  const lowerTerm = trimmed.toLowerCase();
  const regulationFields = buildBriefFields();
  const articleFields = buildBriefFields();

  // Rank: entities matching in MORE fields first (e.g. description AND
  // product expertise both hitting "medical"), alphabetical within the
  // same match count.
  const rank = results => results.sort((a, b) => b.matchedFields.length - a.matchedFields.length || a.title.localeCompare(b.title));

  // Organizations (always searchable)
  {
    const results = [];
    for (const org of datasets.organizations ?? []) {
      const matched = collectMatches(org, organizationFields, lowerTerm, trimmed);
      if (matched.length) {
        results.push({
          type: "organization",
          id: String(org.id || org.slug),
          title: org.orgName || "Untitled Organization",
          url: organizationUrl(org),
          matchedFields: matched
        });
      }
    }
    sections.push({
      type: "organization",
      label: "Organizations",
      results: rank(results)
    });
  }

  // Events
  if (visibility.eventsVisible) {
    const results = [];
    for (const evt of datasets.events ?? []) {
      const matched = collectMatches(evt, eventFields, lowerTerm, trimmed);
      if (matched.length) {
        results.push({
          type: "event",
          id: String(evt.id || evt.slug),
          title: evt.title || "Untitled Event",
          url: eventUrl(evt),
          matchedFields: matched
        });
      }
    }
    sections.push({
      type: "event",
      label: "Events",
      results: rank(results)
    });
  }

  // Regulations
  if (visibility.regulationsVisible) {
    const results = [];
    for (const reg of datasets.regulations ?? []) {
      const matched = collectMatches(reg, regulationFields, lowerTerm, trimmed);
      if (matched.length) {
        results.push({
          type: "regulation",
          id: String(reg.id || reg.slug),
          title: reg.title || "Untitled Regulation",
          url: regulationUrl(reg),
          matchedFields: matched
        });
      }
    }
    sections.push({
      type: "regulation",
      label: "Regulations",
      results: rank(results)
    });
  }

  // Articles
  if (visibility.articlesVisible) {
    const results = [];
    for (const art of datasets.articles ?? []) {
      const matched = collectMatches(art, articleFields, lowerTerm, trimmed);
      if (matched.length) {
        results.push({
          type: "article",
          id: String(art.id || art.slug),
          title: art.title || "Untitled Article",
          url: articleUrl(art),
          matchedFields: matched
        });
      }
    }
    sections.push({
      type: "article",
      label: "Articles",
      results: rank(results)
    });
  }
  return sections;
}